import { createFileRoute, useBlocker, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  Loader2,
  Mail,
  Smartphone,
  Monitor,
  Maximize,
  Minimize,
  Send,
  Upload,
  Wand2,
  AlertTriangle,
  RotateCcw,
  Home,
  Braces,
  Save,
} from "lucide-react";
import { listSmtpAccounts, sendSmtpMail, createSmtpAccount } from "@/lib/smtp-accounts.functions";
import { HTML_TEMPLATES } from "@/lib/html-templates";
import { WizardCoach } from "@/components/app/wizard-coach";
import type { CoachResult, CoachAction } from "@/components/app/wizard-coach";
import { coachStep } from "@/lib/wizard-coach.functions";
import { ShieldAlert, Wand2 as Wand2Icon } from "lucide-react";
import { getCampaignReadiness } from "@/lib/campaign-readiness.functions";
import type { ReadinessCheck } from "@/lib/campaign-readiness.functions";

export const Route = createFileRoute("/_authenticated/campaign-wizard")({
  component: CampaignWizardPage,
  head: () => ({ meta: [{ title: "Kampagnen-Wizard — InboxPulse" }] }),
  errorComponent: WizardErrorBoundary,
  notFoundComponent: WizardNotFound,
});

function WizardErrorBoundary({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  console.error("Campaign Wizard Error:", error);

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <PageHeader
        title="Kampagnen-Wizard"
        description="Etwas ist schiefgelaufen beim Laden des Wizards."
      />
      <Card className="p-8 text-center space-y-6">
        <div className="mx-auto size-16 rounded-full bg-destructive/10 grid place-items-center">
          <AlertTriangle className="size-8 text-destructive" />
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-semibold tracking-tight">Fehler im Kampagnen-Wizard</h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Ein unerwarteter Fehler ist aufgetreten. Du kannst den Wizard neu laden oder zur
            Startseite zurückkehren.
          </p>
        </div>
        {error.message && (
          <div className="mx-auto max-w-md rounded-lg border border-destructive/20 bg-destructive/5 p-3">
            <p className="text-xs font-mono text-destructive break-all">{error.message}</p>
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-3">
          <Button
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            <RotateCcw className="size-4 mr-2" />
            Wizard neu laden
          </Button>
          <Button variant="outline" onClick={() => router.navigate({ to: "/" })}>
            <Home className="size-4 mr-2" />
            Zur Startseite
          </Button>
        </div>
      </Card>
    </div>
  );
}

function WizardNotFound() {
  const router = useRouter();
  return (
    <div className="p-8 max-w-4xl mx-auto">
      <PageHeader title="Kampagnen-Wizard" description="Seite nicht gefunden." />
      <Card className="p-8 text-center space-y-6">
        <div className="space-y-2">
          <h2 className="text-xl font-semibold tracking-tight">Seite nicht gefunden</h2>
          <p className="text-sm text-muted-foreground">
            Der angeforderte Wizard-Schritt existiert nicht.
          </p>
        </div>
        <Button onClick={() => router.navigate({ to: "/campaign-wizard" })}>
          <RotateCcw className="size-4 mr-2" />
          Zum Wizard
        </Button>
      </Card>
    </div>
  );
}

const TRACKING_ORIGIN = "https://deliverability-delight.lovable.app";
const STEPS = [
  "Name",
  "Tracking-Snippet",
  "Empfänger",
  "Betreff-Rotation",
  "HTML-Letter",
  "Versand",
] as const;

function parseCsvLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((s) => s.trim().replace(/^"|"$/g, ""))
    .filter(Boolean);
}

type RecipientRecord = { email: string; fields: Record<string, string> };

type MimeValidationResult = {
  matches: boolean;
  expectedBytes: number;
  actualBytes: number;
  expectedSha256: string;
  actualSha256: string;
  firstDifference: number | null;
  expectedExcerpt?: string;
  actualExcerpt?: string;
};

function normalizePlaceholder(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function splitCsvRow(row: string, delimiter: string): string[] {
  const cells: string[] = [];
  let value = "";
  let quoted = false;
  for (let i = 0; i < row.length; i += 1) {
    const char = row[i];
    if (char === '"' && quoted && row[i + 1] === '"') {
      value += '"';
      i += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === delimiter && !quoted) {
      cells.push(value.trim());
      value = "";
    } else {
      value += char;
    }
  }
  cells.push(value.trim());
  return cells;
}

function parseRecipientCsv(text: string): { records: RecipientRecord[]; placeholders: string[] } {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (!lines.length) return { records: [], placeholders: [] };
  const firstLine = lines[0] ?? "";
  const delimiter =
    [",", ";", "\t"].sort((a, b) => firstLine.split(b).length - firstLine.split(a).length)[0] ??
    ",";
  const firstCells = splitCsvRow(firstLine, delimiter);
  const normalizedFirst = firstCells.map(normalizePlaceholder);
  const emailAliases = ["email", "e_mail", "recipient_email", "recipient", "lead_email"];
  const emailIndex = normalizedFirst.findIndex((cell) => emailAliases.includes(cell));
  const hasHeader = emailIndex >= 0;
  const headers = hasHeader
    ? normalizedFirst.map((header, index) => header || `spalte_${index + 1}`)
    : ["email"];
  const startIndex = hasHeader ? 1 : 0;
  const actualEmailIndex = hasHeader ? emailIndex : 0;
  const byEmail = new Map<string, RecipientRecord>();

  for (let i = startIndex; i < lines.length; i += 1) {
    const cells = splitCsvRow(lines[i] ?? "", delimiter);
    const email = (cells[actualEmailIndex] ?? "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) continue;
    const fields: Record<string, string> = { email };
    headers.forEach((header, index) => {
      if (header) fields[header] = cells[index]?.trim() ?? "";
    });
    byEmail.set(email, { email, fields });
  }

  return {
    records: Array.from(byEmail.values()),
    placeholders: Array.from(new Set(headers.filter((header) => !emailAliases.includes(header)))),
  };
}

function personalize(value: string, fields: Record<string, string>): string {
  return value.replace(/\{\{\s*([a-zA-Z0-9_äöüÄÖÜß.-]+)\s*\}\}/g, (match, key: string) => {
    const normalizedKey = normalizePlaceholder(key);
    return Object.prototype.hasOwnProperty.call(fields, normalizedKey)
      ? fields[normalizedKey]
      : match;
  });
}

function injectPixelLocal(html: string, pixelUrl: string): string {
  const img = `<img src="${pixelUrl}" width="1" height="1" alt="" style="display:block;border:0;outline:none;width:1px;height:1px" />`;
  if (/<img[^>]+src="[^"]*\/(?:a\/|api\/public\/pixel\/)/i.test(html)) return html;
  if (/<\/body\s*>/i.test(html)) return html.replace(/<\/body\s*>/i, `${img}</body>`);
  return `${html}\n${img}`;
}

function appendHtmlBlock(doc: string, block: string): string {
  if (/<\/body\s*>/i.test(doc)) return doc.replace(/<\/body\s*>/i, `${block}\n</body>`);
  return `${doc}\n${block}`;
}

function addAltsToImages(doc: string): { html: string; count: number } {
  let count = 0;
  const html = doc.replace(/<img\b([^>]*)>/gi, (full, attrs) => {
    if (/\balt\s*=/i.test(attrs)) return full;
    count += 1;
    return `<img${attrs} alt="">`;
  });
  return { html, count };
}

const SPAM_TOKEN_RE =
  /\b(FREE|GRATIS|GARANTIERT|GEWINN|JACKPOT|KOSTENLOS!*|100%|RABATT!+|SOFORT!+|JETZT KAUFEN)\b/gi;
function cleanSubject(s: string): string {
  let out = s.replace(/\$\$+/g, "").replace(SPAM_TOKEN_RE, "").replace(/!{2,}/g, "!");
  // ALL CAPS Wörter (>3 Buchstaben) → Titlecase
  out = out.replace(/\b[A-ZÄÖÜ]{4,}\b/g, (w) => w.charAt(0) + w.slice(1).toLowerCase());
  return out.replace(/\s{2,}/g, " ").trim();
}

function CampaignWizardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Step 1
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<"idle" | "saving" | "saved" | "error">(
    "idle",
  );
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState<Date | null>(null);
  const draftSaveInFlightRef = useRef(false);

  // Warnung beim Verlassen/Neuladen, während Auto-Save läuft
  useBlocker({
    shouldBlockFn: () => autoSaveStatus === "saving",
    enableBeforeUnload: autoSaveStatus === "saving",
  });

  // Step 3
  const [recipientsText, setRecipientsText] = useState("");
  const recipientCsv = useMemo(() => parseRecipientCsv(recipientsText), [recipientsText]);
  const recipients = useMemo(
    () => recipientCsv.records.map((record) => record.email),
    [recipientCsv],
  );

  // Step 4
  const [subjectsText, setSubjectsText] = useState("");
  const subjects = useMemo(() => parseCsvLines(subjectsText), [subjectsText]);

  // Step 5
  const [html, setHtml] = useState(
    `<!doctype html>\n<html><body>\n  <p>Hallo,</p>\n  <p>Ihre Nachricht hier…</p>\n</body></html>`,
  );
  const htmlTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "mobile">("desktop");
  const [previewRecipientIndex, setPreviewRecipientIndex] = useState(0);
  const [previewSearch, setPreviewSearch] = useState("");
  const [previewSearchField, setPreviewSearchField] = useState<string>("all");
  const searchFieldOptions = useMemo(() => {
    const headers = recipientCsv.placeholders;
    return [
      { value: "all", label: "Alle Felder" },
      { value: "email", label: "E-Mail" },
      ...headers.map((h) => ({ value: h, label: h })),
    ];
  }, [recipientCsv.placeholders]);
  const filteredPreviewRecords = useMemo(() => {
    const term = previewSearch.trim().toLowerCase();
    if (!term) return recipientCsv.records;
    return recipientCsv.records.filter((record) => {
      if (previewSearchField === "all") {
        if (record.email.toLowerCase().includes(term)) return true;
        return Object.values(record.fields).some((v) => String(v).toLowerCase().includes(term));
      }
      if (previewSearchField === "email") {
        return record.email.toLowerCase().includes(term);
      }
      const value = record.fields[previewSearchField];
      return String(value ?? "")
        .toLowerCase()
        .includes(term);
    });
  }, [recipientCsv.records, previewSearch, previewSearchField]);
  const previewRecipient =
    filteredPreviewRecords.find(
      (record) => recipientCsv.records.indexOf(record) === previewRecipientIndex,
    ) ??
    filteredPreviewRecords[0] ??
    null;
  const personalizedPreviewHtml = previewRecipient
    ? personalize(html, previewRecipient.fields)
    : html;

  useEffect(() => {
    if (!isPreviewFullscreen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsPreviewFullscreen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isPreviewFullscreen]);

  // Step 6
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [rotationMode, setRotationMode] = useState<"round_robin" | "random">("round_robin");
  const [replyTo, setReplyTo] = useState("");
  const [sending, setSending] = useState(false);
  const [throttleMs, setThrottleMs] = useState(1500);
  const stopRequestedRef = useRef(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [mimeValidation, setMimeValidation] = useState<MimeValidationResult | null>(null);
  const [progress, setProgress] = useState<{ done: number; ok: number; fail: number }>({
    done: 0,
    ok: 0,
    fail: 0,
  });

  // Tracking & Pacing (auf campaigns gespeichert, lazy via Supabase)
  const [trackingDomain, setTrackingDomain] = useState("");
  const [useStealth, setUseStealth] = useState(true);
  const [dailyLimit, setDailyLimit] = useState(50);
  const [trackingDirty, setTrackingDirty] = useState(false);
  const [pacingDirty, setPacingDirty] = useState(false);

  async function saveTracking() {
    if (!campaignId) return;
    const cleaned = trackingDomain
      .trim()
      .replace(/^https?:\/\//, "")
      .replace(/\/$/, "");
    const { error } = await supabase
      .from("campaigns")
      .update({
        tracking_domain: cleaned || null,
        use_stealth_pixel: useStealth,
      })
      .eq("id", campaignId);
    if (error) {
      toast.error(error.message);
      return;
    }
    setTrackingDirty(false);
    toast.success("Tracking gespeichert");
  }

  async function savePacing() {
    if (!campaignId) return;
    const { error } = await supabase
      .from("campaigns")
      .update({ daily_send_limit: Math.max(1, Math.min(100000, dailyLimit)) })
      .eq("id", campaignId);
    if (error) {
      toast.error(error.message);
      return;
    }
    setPacingDirty(false);
    toast.success("Tageslimit gespeichert");
  }

  // Pre-Send-Checklist
  const fetchReadiness = useServerFn(getCampaignReadiness);
  const { data: readiness, refetch: refetchReadiness } = useQuery({
    queryKey: ["campaign-readiness", campaignId],
    queryFn: () => fetchReadiness({ data: { campaign_id: campaignId! } }),
    enabled: !!campaignId && step === 5,
  });

  // Inbox-Coach Pre-Send-Guard
  const [coachResult, setCoachResult] = useState<CoachResult | null>(null);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightResult, setPreflightResult] = useState<CoachResult | null>(null);
  const coachFn = useServerFn(coachStep);

  const trackingOrigin = trackingDomain.trim()
    ? `https://${trackingDomain.trim()}`
    : TRACKING_ORIGIN;
  const pixelUrl = campaignId ? `${trackingOrigin}/api/public/pixel/${campaignId}.gif` : "";
  const pixelImg = pixelUrl
    ? `<img src="${pixelUrl}" width="1" height="1" alt="" style="display:block;border:0;outline:none" />`
    : "";

  const listAccounts = useServerFn(listSmtpAccounts);
  const createAccount = useServerFn(createSmtpAccount);
  const queryClient = useQueryClient();
  const { data: accountsRes } = useQuery({
    queryKey: ["smtp-accounts-wizard"],
    queryFn: () => listAccounts({ data: {} as never }),
    enabled: step >= 4,
  });
  const accounts = (accountsRes as { accounts?: any[] } | undefined)?.accounts ?? [];

  // Quick-Create SMTP (Presets)
  const SMTP_PRESETS = {
    mailersend: {
      label: "MailerSend",
      host: "smtp.mailersend.net",
      port: 587,
      tls: true,
      user: "",
      pass: "",
      from: "",
    },
    sendgrid: {
      label: "SendGrid",
      host: "smtp.sendgrid.net",
      port: 587,
      tls: true,
      user: "apikey",
      pass: "",
      from: "",
    },
    kanzlei: {
      label: "Kanzlei Rechtspartnerschaft",
      host: "mail.kanzleirechtspartnerschaft.de",
      port: 465,
      tls: true,
      user: "mailuser",
      pass: "",
      from: "info@kanzleirechtspartnerschaft.de",
    },
    custom: {
      label: "Custom",
      host: "",
      port: 587,
      tls: true,
      user: "",
      pass: "",
      from: "",
    },
  } as const;
  type PresetKey = keyof typeof SMTP_PRESETS;

  const [quickOpen, setQuickOpen] = useState(false);
  const [qPreset, setQPreset] = useState<PresetKey>("mailersend");
  const [qLabel, setQLabel] = useState<string>(SMTP_PRESETS.mailersend.label);
  const [qHost, setQHost] = useState<string>(SMTP_PRESETS.mailersend.host);
  const [qPort, setQPort] = useState<number>(SMTP_PRESETS.mailersend.port);
  const [qTls, setQTls] = useState<boolean>(SMTP_PRESETS.mailersend.tls);
  const [qUser, setQUser] = useState<string>(SMTP_PRESETS.mailersend.user);
  const [qPass, setQPass] = useState<string>(SMTP_PRESETS.mailersend.pass);
  const [qFromEmail, setQFromEmail] = useState<string>(SMTP_PRESETS.mailersend.from);
  const [qFromName, setQFromName] = useState("");
  const [qSaving, setQSaving] = useState(false);

  function applyPreset(key: PresetKey) {
    const p = SMTP_PRESETS[key];
    setQPreset(key);
    setQLabel(p.label);
    setQHost(p.host);
    setQPort(p.port);
    setQTls(p.tls);
    setQUser(p.user);
    setQPass(p.pass);
    setQFromEmail(p.from);
  }

  async function handleQuickCreate() {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(qFromEmail)) {
      toast.error("Bitte eine gültige From-Adresse eingeben");
      return;
    }
    setQSaving(true);
    try {
      await createAccount({
        data: {
          label: qLabel,
          smtp_host: qHost,
          smtp_port: qPort,
          smtp_tls: qTls,
          smtp_username: qUser,
          password: qPass,
          from_email: qFromEmail,
          from_name: qFromName || null,
        } as never,
      });
      toast.success("SMTP-Konto angelegt");
      setQuickOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["smtp-accounts-wizard"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Anlegen fehlgeschlagen");
    } finally {
      setQSaving(false);
    }
  }

  async function handleCreatePreset(key: PresetKey) {
    const p = SMTP_PRESETS[key];
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.from)) {
      // Preset hat keine gültige From-Adresse → Dialog vorausgefüllt öffnen
      applyPreset(key);
      setQuickOpen(true);
      return;
    }
    setQSaving(true);
    try {
      await createAccount({
        data: {
          label: p.label,
          smtp_host: p.host,
          smtp_port: p.port,
          smtp_tls: p.tls,
          smtp_username: p.user,
          password: p.pass,
          from_email: p.from,
          from_name: null,
        } as never,
      });
      toast.success(`${p.label}-Konto angelegt`);
      await queryClient.invalidateQueries({ queryKey: ["smtp-accounts-wizard"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Anlegen fehlgeschlagen");
    } finally {
      setQSaving(false);
    }
  }

  const sendOne = useServerFn(sendSmtpMail);

  function toggleAccount(id: string) {
    setAccountIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  // SMTP-Connectivity-Test: pro Konto eine Testmail senden und Ergebnis anzeigen.
  type SmtpTestState = { status: "pending" | "ok" | "fail"; msg?: string };
  const [smtpTests, setSmtpTests] = useState<Record<string, SmtpTestState>>({});
  const [testingAll, setTestingAll] = useState(false);

  async function handleTestAllSmtp() {
    const target = (testEmail || user?.email || "").trim();
    if (!target || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      toast.error("Bitte oben eine gültige Test-E-Mail eintragen");
      return;
    }
    if (!accounts.length) {
      toast.error("Keine SMTP-Konten vorhanden");
      return;
    }
    setTestingAll(true);
    const initial: Record<string, SmtpTestState> = {};
    for (const a of accounts) initial[a.id] = { status: "pending" };
    setSmtpTests(initial);

    const results = await Promise.all(
      accounts.map(async (a: any) => {
        try {
          const res: any = await sendOne({
            data: {
              account_id: a.id,
              to: target,
              subject: `[SMTP-Test] ${a.label}`,
              html: `<p>SMTP-Connectivity-Test über <b>${a.label}</b> – Versand bestätigt.</p>`,
              text: `SMTP-Connectivity-Test über ${a.label} – Versand bestätigt.`,
            } as never,
          });
          if (!res?.ok) throw new Error(res?.error ?? "Fehler");
          return {
            id: a.id,
            label: a.label,
            state: {
              status: "ok" as const,
              msg: res?.smtp_response || res?.message_id || "gesendet",
            },
          };
        } catch (e: any) {
          return {
            id: a.id,
            label: a.label,
            state: { status: "fail" as const, msg: e?.message ?? "Fehler" },
          };
        }
      }),
    );

    const next: Record<string, SmtpTestState> = {};
    let ok = 0;
    let fail = 0;
    for (const r of results) {
      next[r.id] = r.state;
      if (r.state.status === "ok") ok += 1;
      else fail += 1;
    }
    setSmtpTests(next);
    setTestingAll(false);
    if (fail === 0)
      toast.success(`Alle ${ok} SMTP-Konten funktionieren – Testmails an ${target} gesendet`);
    else if (ok === 0) toast.error(`Alle ${fail} SMTP-Tests fehlgeschlagen`);
    else toast.warning(`${ok} OK · ${fail} Fehler – Details siehe Liste`);
  }

  function pickAccount(index: number): string {
    if (accountIds.length === 0) return "";
    if (accountIds.length === 1) return accountIds[0];
    if (rotationMode === "random") {
      return accountIds[Math.floor(Math.random() * accountIds.length)];
    }
    return accountIds[index % accountIds.length];
  }

  // Test-Mail-Feld einmalig mit eigener Adresse vorbefüllen
  useEffect(() => {
    if (!testEmail && user?.email) setTestEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  async function sendPersonalizedTest() {
    const target = (testEmail || user?.email || "").trim();
    if (!target || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      toast.error("Bitte eine gültige Test-E-Mail eintragen");
      return;
    }
    if (!accountIds.length) {
      toast.error("Bitte zuerst ein SMTP-Konto wählen");
      return;
    }
    if (!previewRecipient) {
      toast.error("Bitte einen Empfänger für die Platzhalter-Vorschau auswählen");
      return;
    }
    const previewSubject = personalize(
      subjects[0] ?? (name || "Kampagnen-Test"),
      previewRecipient.fields,
    );
    const previewHtml = personalize(html, previewRecipient.fields);
    setSendingTest(true);
    setMimeValidation(null);
    try {
      const result = await sendOne({
        data: {
          account_id: accountIds[0],
          to: target,
          subject: `[TEST] ${previewSubject}`,
          html: previewHtml,
          expected_html: previewHtml,
          reply_to: replyTo.trim() || undefined,
          campaign_id: null,
        },
      });
      if (!result.ok) throw new Error(result.error ?? "Test-Mail fehlgeschlagen");
      const validation = result.mime_validation as MimeValidationResult | null;
      setMimeValidation(validation);
      if (validation?.matches) {
        toast.success(
          `Test-Mail gesendet – MIME-Body entspricht exakt der Vorschau (${validation.actualBytes} Bytes)`,
        );
      } else if (validation) {
        toast.warning("Test-Mail gesendet, aber der MIME-Body weicht von der Vorschau ab");
      } else {
        toast.warning("Test-Mail gesendet, MIME-Validierung lieferte jedoch kein Ergebnis");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Test-Mail fehlgeschlagen");
    } finally {
      setSendingTest(false);
    }
  }

  const createCampaign = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .insert({
          name,
          description: desc,
          reply_to: replyTo.trim().toLowerCase() || null,
          status: "draft",
          user_id: user!.id,
        })
        .select("id")
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: (id) => {
      setCampaignId(id);
      toast.success("Kampagne angelegt");
      setStep(1);
    },
    onError: (e: any) => toast.error(e.message),
  });

  async function persistDraft(existingCampaignId?: string | null) {
    if (!user) throw new Error("Bitte zuerst anmelden");
    if (!name.trim()) throw new Error("Bitte gib der Kampagne zuerst einen Namen");
    if (draftSaveInFlightRef.current) throw new Error("Entwurf wird bereits gespeichert");

    draftSaveInFlightRef.current = true;
    try {
      let draftId = existingCampaignId;
      if (!draftId) {
        const { data, error } = await supabase
          .from("campaigns")
          .insert({
            name: name.trim(),
            description: desc.trim() || null,
            html_content: html,
            reply_to: replyTo.trim().toLowerCase() || null,
            status: "draft",
            user_id: user.id,
          })
          .select("id")
          .single();
        if (error) throw error;
        draftId = data.id;
        setCampaignId(draftId);
      } else {
        const { error } = await supabase
          .from("campaigns")
          .update({
            name: name.trim(),
            description: desc.trim() || null,
            html_content: html,
            reply_to: replyTo.trim().toLowerCase() || null,
            status: "draft",
            tracking_domain:
              trackingDomain
                .trim()
                .replace(/^https?:\/\//, "")
                .replace(/\/$/, "") || null,
            use_stealth_pixel: useStealth,
            daily_send_limit: Math.max(1, Math.min(100000, dailyLimit)),
          })
          .eq("id", draftId);
        if (error) throw error;
      }

      const [recipientDelete, subjectDelete] = await Promise.all([
        supabase.from("campaign_recipients").delete().eq("campaign_id", draftId),
        supabase.from("campaign_subjects").delete().eq("campaign_id", draftId),
      ]);
      if (recipientDelete.error) throw recipientDelete.error;
      if (subjectDelete.error) throw subjectDelete.error;

      const writes: Array<PromiseLike<{ error: { message: string } | null }>> = [];
      if (recipientCsv.records.length) {
        writes.push(
          supabase.from("campaign_recipients").insert(
            recipientCsv.records.map((record, position) => ({
              campaign_id: draftId,
              user_id: user.id,
              email: record.email,
              fields: record.fields,
              position,
            })),
          ),
        );
      }
      if (subjects.length) {
        writes.push(
          supabase.from("campaign_subjects").insert(
            subjects.map((subject, position) => ({
              campaign_id: draftId,
              user_id: user.id,
              subject,
              position,
            })),
          ),
        );
      }
      const results = await Promise.all(writes);
      const writeError = results.find((result) => result.error)?.error;
      if (writeError) throw writeError;
      return draftId;
    } finally {
      draftSaveInFlightRef.current = false;
    }
  }

  const saveDraft = useMutation({
    mutationFn: () => persistDraft(campaignId),
    onSuccess: async () => {
      setTrackingDirty(false);
      setPacingDirty(false);
      await queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success("Entwurf vollständig gespeichert – du kannst später weiterarbeiten");
    },
    onError: (error) =>
      toast.error(
        error instanceof Error ? error.message : "Entwurf konnte nicht gespeichert werden",
      ),
  });

  const autoSaveDraft = useMutation({
    mutationFn: () => persistDraft(campaignId),
    onMutate: () => setAutoSaveStatus("saving"),
    onSuccess: async () => {
      setTrackingDirty(false);
      setPacingDirty(false);
      setAutoSaveStatus("saved");
      setLastAutoSavedAt(new Date());
      await queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (error) => {
      if (error instanceof Error && error.message === "Entwurf wird bereits gespeichert") return;
      setAutoSaveStatus("error");
    },
  });

  useEffect(() => {
    if (!user || !name.trim() || sending) return;
    setAutoSaveStatus("idle");
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const saveWhenReady = () => {
      if (draftSaveInFlightRef.current) {
        retryTimer = setTimeout(saveWhenReady, 500);
        return;
      }
      autoSaveDraft.mutate();
    };
    const timer = setTimeout(saveWhenReady, 1500);
    return () => {
      clearTimeout(timer);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [
    name,
    desc,
    recipientsText,
    subjectsText,
    html,
    trackingDomain,
    useStealth,
    dailyLimit,
    user,
    sending,
    campaignId,
  ]);

  async function saveCurrentCampaignContent() {
    if (!campaignId || !user) return;
    if (step === 2) {
      const { error: deleteError } = await supabase
        .from("campaign_recipients")
        .delete()
        .eq("campaign_id", campaignId);
      if (deleteError) throw deleteError;
      if (recipientCsv.records.length) {
        const { error } = await supabase.from("campaign_recipients").insert(
          recipientCsv.records.map((record, position) => ({
            campaign_id: campaignId,
            user_id: user.id,
            email: record.email,
            fields: record.fields,
            position,
          })),
        );
        if (error) throw error;
      }
    }
    if (step === 3) {
      const { error: deleteError } = await supabase
        .from("campaign_subjects")
        .delete()
        .eq("campaign_id", campaignId);
      if (deleteError) throw deleteError;
      if (subjects.length) {
        const { error } = await supabase.from("campaign_subjects").insert(
          subjects.map((subject, position) => ({
            campaign_id: campaignId,
            user_id: user.id,
            subject,
            position,
          })),
        );
        if (error) throw error;
      }
    }
    if (step === 4) {
      const { error } = await supabase
        .from("campaigns")
        .update({ html_content: html })
        .eq("id", campaignId);
      if (error) throw error;
    }
  }

  async function goToNextStep() {
    try {
      await saveCurrentCampaignContent();
      setStep((current) => current + 1);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Kampagneninhalt konnte nicht gespeichert werden",
      );
    }
  }

  function copy(text: string) {
    navigator.clipboard.writeText(text).then(() => toast.success("Kopiert"));
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>, setter: (s: string) => void) {
    const f = e.target.files?.[0];
    if (!f) return;
    const name = f.name.toLowerCase();
    if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
      toast.error("Excel-Dateien werden nicht unterstützt. Bitte als CSV (UTF-8) exportieren.");
      e.target.value = "";
      return;
    }
    try {
      let txt = await f.text();
      // BOM entfernen
      if (txt.charCodeAt(0) === 0xfeff) txt = txt.slice(1);
      if (!txt.trim()) {
        toast.error("Datei ist leer.");
        e.target.value = "";
        return;
      }
      setter(txt);
      // Feedback: wie viele Emails wurden erkannt
      const isRecipients = setter === setRecipientsText;
      if (isRecipients) {
        const parsedCsv = parseRecipientCsv(txt);
        const found = parsedCsv.records.length;
        if (found === 0) {
          toast.warning(`Datei "${f.name}" geladen, aber keine gültigen E-Mail-Adressen gefunden.`);
        } else {
          toast.success(
            `${found} E-Mail-Adresse${found === 1 ? "" : "n"} und ${parsedCsv.placeholders.length} Platzhalter aus "${f.name}" geladen.`,
          );
        }
      } else {
        const lines = parseCsvLines(txt).length;
        toast.success(`${lines} Zeile${lines === 1 ? "" : "n"} aus "${f.name}" geladen.`);
      }
    } catch (err) {
      console.error("[campaign-wizard] CSV upload failed", err);
      toast.error(`Datei konnte nicht gelesen werden: ${(err as Error).message}`);
    } finally {
      e.target.value = "";
    }
  }

  function insertPlaceholder(placeholder: string) {
    const token = `{{${placeholder}}}`;
    const textarea = htmlTextareaRef.current;
    if (!textarea) {
      setHtml((current) => `${current}${token}`);
      return;
    }
    const start = textarea.selectionStart ?? html.length;
    const end = textarea.selectionEnd ?? start;
    setHtml((current) => `${current.slice(0, start)}${token}${current.slice(end)}`);
    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + token.length, start + token.length);
    });
    toast.success(`${token} eingefügt`);
  }

  async function runSend() {
    if (!accountIds.length) return toast.error("Bitte mindestens ein SMTP-Konto wählen");
    if (!recipients.length) return toast.error("Keine Empfänger");
    if (!subjects.length) return toast.error("Keine Betreffzeilen");
    if (!campaignId) return toast.error("Kampagne fehlt");
    setSending(true);
    stopRequestedRef.current = false;
    setProgress({ done: 0, ok: 0, fail: 0 });
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < recipients.length; i++) {
      if (stopRequestedRef.current) break;
      const record = recipientCsv.records[i];
      if (!record) continue;
      const to = record.email;
      const subject = personalize(subjects[i % subjects.length] ?? "", record.fields);
      const acc = pickAccount(i);
      try {
        await sendOne({
          data: {
            account_id: acc,
            to,
            subject,
            html: personalize(html, record.fields),
            campaign_id: campaignId,
          },
        });
        ok++;
      } catch (err: any) {
        fail++;
        console.error("send fail", to, err?.message);
      }
      setProgress({ done: i + 1, ok, fail });
      if (i < recipients.length - 1 && throttleMs > 0) {
        await new Promise((r) => setTimeout(r, throttleMs));
      }
    }
    setSending(false);
    toast.success(`Versand abgeschlossen: ${ok} ok, ${fail} fehlgeschlagen`);
  }

  function isCoachRisky(r: CoachResult | null) {
    if (!r) return true; // noch keine Analyse → lieber prüfen
    if (r.score < 70) return true;
    return r.issues.some((i) => i.severity === "critical" || i.severity === "high");
  }

  function applyFix(action: CoachAction) {
    try {
      switch (action.type) {
        case "append_html_block": {
          if (!action.html) return;
          const unsubUrl = campaignId
            ? `${trackingOrigin}/api/public/u/${campaignId}`
            : "{{unsubscribe_url}}";
          const block = action.html.replace(/\{\{unsubscribe_url\}\}/g, unsubUrl);
          setHtml((prev) => appendHtmlBlock(prev, block));
          toast.success("HTML-Block eingefügt (vor </body>)");
          break;
        }
        case "replace_html": {
          if (!action.html) return;
          const unsubUrl = campaignId
            ? `${trackingOrigin}/api/public/u/${campaignId}`
            : "{{unsubscribe_url}}";
          setHtml(action.html.replace(/\{\{unsubscribe_url\}\}/g, unsubUrl));
          toast.success("HTML-Letter durch sauberen Entwurf ersetzt");
          break;
        }
        case "add_image_alts": {
          setHtml((prev) => {
            const r = addAltsToImages(prev);
            toast.success(
              `alt-Attribut zu ${r.count} Bild${r.count === 1 ? "" : "ern"} hinzugefügt`,
            );
            return r.html;
          });
          break;
        }
        case "strip_subject_spam": {
          setSubjectsText((prev) => {
            const lines = prev.split(/\r?\n/);
            const cleaned = lines.map((l) => (l.trim() ? cleanSubject(l) : l));
            toast.success("Spam-Wörter aus Betreffen entfernt");
            return cleaned.join("\n");
          });
          break;
        }
        case "replace_subjects": {
          if (!action.subjects?.length) return;
          setSubjectsText(action.subjects.join("\n"));
          toast.success(`${action.subjects.length} neue Betreffvarianten gesetzt`);
          break;
        }
        case "append_subjects": {
          if (!action.subjects?.length) return;
          setSubjectsText((prev) => {
            const sep = prev && !prev.endsWith("\n") ? "\n" : "";
            return prev + sep + action.subjects!.join("\n");
          });
          toast.success(`${action.subjects.length} Betreffvarianten ergänzt`);
          break;
        }
        case "set_throttle_ms": {
          if (typeof action.ms !== "number") return;
          setThrottleMs(action.ms);
          toast.success(`Versand-Pause auf ${action.ms} ms gesetzt`);
          break;
        }
      }
    } catch (e: any) {
      console.error("[wizard] applyFix failed", e);
      toast.error(e?.message ?? "Fix konnte nicht angewendet werden");
    }
  }

  async function attemptSend() {
    if (!accountIds.length) return toast.error("Bitte mindestens ein SMTP-Konto wählen");
    if (!recipients.length) return toast.error("Keine Empfänger");
    if (!subjects.length) return toast.error("Keine Betreffzeilen");
    if (!campaignId) return toast.error("Kampagne fehlt");

    // Wenn Coach bereits ein aktuelles, gutes Ergebnis hat → direkt senden
    if (coachResult && !isCoachRisky(coachResult)) {
      return runSend();
    }

    // Sonst: KI-Pre-Flight-Check
    setPreflightOpen(true);
    setPreflightLoading(true);
    setPreflightResult(coachResult);
    try {
      const r = (await coachFn({
        data: {
          step: 5,
          name,
          desc,
          recipientsCount: recipients.length,
          recipientsSample: recipients.slice(0, 5),
          subjects,
          htmlSnippet: html,
          smtpAccountsCount: accountIds.length,
          throttleMs,
        },
      })) as CoachResult;
      setPreflightResult(r);
      setCoachResult(r);
    } catch (e: any) {
      toast.error(e?.message ?? "KI-Check fehlgeschlagen");
      setPreflightOpen(false);
    } finally {
      setPreflightLoading(false);
    }
  }

  const canNext = (() => {
    switch (step) {
      case 0:
        return !!campaignId; // moves to next via createCampaign success
      case 1:
        return !!campaignId;
      case 2:
        return recipients.length > 0;
      case 3:
        return subjects.length > 0;
      case 4:
        return html.trim().length > 0;
      default:
        return false;
    }
  })();

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <PageHeader
        title="Kampagne starten (Wizard)"
        description="Schritt für Schritt: Name → Tracking → Empfänger → Betreffs → HTML → Versand."
        actions={
          <div className="flex items-center gap-3">
            {name.trim() && (
              <span
                className={
                  autoSaveStatus === "error"
                    ? "text-xs text-destructive"
                    : "text-xs text-muted-foreground"
                }
                role="status"
              >
                {autoSaveStatus === "saving" && "Wird automatisch gespeichert …"}
                {autoSaveStatus === "saved" &&
                  `Automatisch gespeichert${lastAutoSavedAt ? ` · ${lastAutoSavedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}` : ""}`}
                {autoSaveStatus === "error" && "Auto-Save fehlgeschlagen"}
                {autoSaveStatus === "idle" && "Ungespeicherte Änderungen"}
              </span>
            )}
            <Button
              variant="outline"
              onClick={() => saveDraft.mutate()}
              disabled={!name.trim() || saveDraft.isPending || sending}
            >
              {saveDraft.isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Save className="mr-2 size-4" />
              )}
              Entwurf speichern
            </Button>
          </div>
        }
      />

      <HelpPanel
        title="Hilfe: So baust du eine Kampagne (Schritt für Schritt)"
        what={
          <>
            Dieser Wizard führt dich in <b>6 Schritten</b> durch eine komplette Mail-Kampagne — du
            musst nichts vorher wissen. Du klickst dich von Schritt zu Schritt, oben siehst du immer
            wo du gerade bist.
          </>
        }
        why={
          <>
            Eine Kampagne ist eine Mail, die du an viele Leute schickst. Damit es nicht im Spam
            landet und du sehen kannst, wer sie geöffnet hat, sind ein paar Einstellungen nötig —
            der Wizard macht das für dich.
          </>
        }
        how={[
          <>
            <b>Schritt 1 — Name:</b> Gib der Kampagne einen Namen, z.B. "Newsletter Juni" oder
            "Aktion Sommer". Nur für dich, der Empfänger sieht ihn nicht.
          </>,
          <>
            <b>Schritt 2 — Tracking:</b> Wähle, ob du nur Gesamt-Öffnungen messen willst (max.
            Zustellbarkeit) oder pro Empfänger (für A/B-Tests). Wenn unsicher: nimm{" "}
            <b>"Pro Kampagne"</b>.
          </>,
          <>
            <b>Schritt 3 — Empfänger:</b> Wähle eine Empfänger-Liste, die du vorher unter{" "}
            <b>Empfänger importieren</b> hochgeladen hast. Noch keine? Erst Liste anlegen.
          </>,
          <>
            <b>Schritt 4 — Betreff:</b> Schreibe 1-3 Betreffzeilen. Mit mehreren kannst du testen,
            welcher am besten geöffnet wird. Kurz, neugierig, kein "GRATIS!!!".
          </>,
          <>
            <b>Schritt 5 — HTML:</b> Inhalt der Mail einfügen. Das System baut Tracking-Pixel und
            Abmelde-Link automatisch ein.
          </>,
          <>
            <b>Schritt 6 — Versand:</b> Wähle Absender(adresse) und drücke <b>Senden</b>. Status
            siehst du danach im Dashboard.
          </>,
        ]}
        tips={[
          <>
            Lieber zuerst eine kleine Test-Liste (10 Empfänger) nehmen, bevor du an 1000 Leute
            sendest.
          </>,
          <>
            Wörter wie "GRATIS", "Jetzt kaufen!!!" oder viele Ausrufezeichen triggern Spam-Filter —
            vermeiden.
          </>,
          <>Pro Tag und Absender nicht mehr als 50-100 Mails am Anfang. Mehr → erst nach Warmup.</>,
          <>
            Bei jedem Schritt: rechts in der Seitenleiste siehst du eine Live-Vorschau, was beim
            Empfänger ankommt.
          </>,
        ]}
        glossary={[
          {
            term: "Tracking-Pixel",
            def: "Ein winziges, unsichtbares Bild in der Mail. Wenn der Empfänger die Mail öffnet, wird das Bild geladen — daran erkennen wir, dass die Mail geöffnet wurde.",
          },
          {
            term: "A/B-Test",
            def: "Du verschickst zwei Varianten (z.B. zwei Betreffzeilen) an je die Hälfte. Danach siehst du, welche besser ankam.",
          },
          {
            term: "Bounce",
            def: "Wenn eine Mail nicht zugestellt werden konnte (z.B. Adresse existiert nicht), kommt eine Fehlermeldung zurück — das nennt man Bounce.",
          },
          {
            term: "Absender",
            def: 'Die E-Mail-Adresse, von der die Mail kommt. Muss vorher unter "Senders" eingerichtet sein.',
          },
        ]}
      />

      {/* Stepper */}
      <div className="mb-6 flex items-center gap-2 flex-wrap">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div
              className={`size-7 rounded-full grid place-items-center text-xs font-semibold border ${
                i < step
                  ? "bg-primary text-primary-foreground border-primary"
                  : i === step
                    ? "bg-accent text-accent-foreground border-accent"
                    : "bg-muted text-muted-foreground border-border"
              }`}
            >
              {i < step ? <Check className="size-3.5" /> : i + 1}
            </div>
            <span className={`text-xs ${i === step ? "font-medium" : "text-muted-foreground"}`}>
              {label}
            </span>
            {i < STEPS.length - 1 && <span className="text-muted-foreground">›</span>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
        <Card className="p-6 bg-card/60 border-border space-y-5">
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">1. Kampagnen-Name</h2>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="z. B. Q2 Outreach Mai 2026"
                />
              </div>
              <div className="space-y-2">
                <Label>Beschreibung (optional)</Label>
                <Input
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  placeholder="Zielgruppe / Variante"
                />
              </div>
              <Button
                onClick={() => createCampaign.mutate()}
                disabled={!name.trim() || createCampaign.isPending || !!campaignId}
                className="w-full"
              >
                {createCampaign.isPending && <Loader2 className="size-4 mr-2 animate-spin" />}
                {campaignId ? "Angelegt — weiter mit ›" : "Kampagne anlegen & weiter"}
              </Button>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">2. Tracking-Snippet</h2>
              <p className="text-sm text-muted-foreground">
                Dieser Tracking-Pixel wird im letzten Schritt automatisch in dein HTML eingebaut. Du
                kannst ihn auch manuell kopieren.
              </p>

              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-amber-700 dark:text-amber-400">
                    Tracking-Subdomain (empfohlen)
                  </Label>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Bei Kaltakquise nie über die Versanddomain tracken. Lege einen CNAME{" "}
                    <code>t.deinedomain.de</code> → <code>cname.lovable.app</code> an und trage die
                    Subdomain hier ein.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="t.meineasykredit.click"
                    value={trackingDomain}
                    onChange={(e) => {
                      setTrackingDomain(e.target.value);
                      setTrackingDirty(true);
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!trackingDirty || !campaignId}
                    onClick={saveTracking}
                  >
                    Speichern
                  </Button>
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={useStealth}
                    onChange={(e) => {
                      setUseStealth(e.target.checked);
                      setTrackingDirty(true);
                    }}
                  />
                  Stealth-Pixel (<code>/a/&lt;token&gt;.png</code>) statt{" "}
                  <code>/api/public/pixel/</code> — weniger Spam-Filter-Erkennung
                </label>
              </div>

              <div className="space-y-2">
                <Label>Pixel-URL</Label>
                <div className="flex gap-2">
                  <Input readOnly value={pixelUrl} className="font-mono text-xs" />
                  <Button variant="outline" size="icon" onClick={() => copy(pixelUrl)}>
                    <Copy className="size-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>HTML-Snippet</Label>
                <Textarea readOnly value={pixelImg} className="font-mono text-xs h-20" />
                <Button variant="outline" size="sm" onClick={() => copy(pixelImg)}>
                  <Copy className="size-3.5 mr-1" /> Snippet kopieren
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">3. Empfänger</h2>
              <p className="text-sm text-muted-foreground">
                Eine E-Mail pro Zeile, oder CSV-Datei hochladen. Duplikate werden entfernt.
              </p>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="rcpt-csv"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-muted/30 cursor-pointer text-sm hover:bg-muted/60"
                >
                  <Upload className="size-4" /> CSV hochladen
                </Label>
                <input
                  id="rcpt-csv"
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={(e) => handleFile(e, setRecipientsText)}
                />
                <span className="text-xs text-muted-foreground">
                  {recipients.length} gültige Empfänger erkannt
                </span>
              </div>
              <Textarea
                value={recipientsText}
                onChange={(e) => setRecipientsText(e.target.value)}
                placeholder={"max@example.com\nanna@example.com"}
                className="font-mono text-xs h-56"
              />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">4. Betreff-Rotation</h2>
              <p className="text-sm text-muted-foreground">
                Eine Betreffzeile pro Zeile, oder CSV-Datei hochladen. Die Betreffs werden im
                Versand rotierend benutzt (Round-Robin).
              </p>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="subj-csv"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-muted/30 cursor-pointer text-sm hover:bg-muted/60"
                >
                  <Upload className="size-4" /> CSV hochladen
                </Label>
                <input
                  id="subj-csv"
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={(e) => handleFile(e, setSubjectsText)}
                />
                <span className="text-xs text-muted-foreground">
                  {subjects.length} Betreffzeile(n)
                </span>
              </div>
              <Textarea
                value={subjectsText}
                onChange={(e) => setSubjectsText(e.target.value)}
                placeholder={"Ihr Angebot heute\nWichtige Info für Sie\nNeu: …"}
                className="font-mono text-xs h-56"
              />
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">5. HTML-Letter</h2>
              <p className="text-sm text-muted-foreground">
                Füge dein HTML ein. Im nächsten Schritt wird der Tracking-Pixel automatisch vor{" "}
                <code>&lt;/body&gt;</code> eingebaut.
              </p>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Vorlagen-Bibliothek
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {HTML_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => {
                        setHtml(tpl.html);
                        if (!subjectsText.trim()) setSubjectsText(tpl.subject);
                        toast.success(`Vorlage "${tpl.name}" eingefügt`);
                      }}
                      className="text-left p-3 rounded-md border border-border bg-muted/20 hover:bg-muted/40 hover:border-primary/50 transition"
                    >
                      <div className="text-sm font-medium">{tpl.name}</div>
                      <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                        {tpl.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs">HTML-Quellcode</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" variant="outline" size="sm">
                          <Braces className="size-3.5" /> Platzhalter
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-64">
                        <DropdownMenuLabel>Felder aus deiner CSV</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => insertPlaceholder("email")}>
                          <span>E-Mail-Adresse</span>
                          <code className="ml-auto text-[10px] text-muted-foreground">
                            {"{{email}}"}
                          </code>
                        </DropdownMenuItem>
                        {recipientCsv.placeholders.map((placeholder) => (
                          <DropdownMenuItem
                            key={placeholder}
                            onSelect={() => insertPlaceholder(placeholder)}
                          >
                            <span className="capitalize">{placeholder.replace(/_/g, " ")}</span>
                            <code className="ml-auto text-[10px] text-muted-foreground">
                              {`{{${placeholder}}}`}
                            </code>
                          </DropdownMenuItem>
                        ))}
                        {!recipientCsv.placeholders.length && (
                          <div className="px-2 py-2 text-xs text-muted-foreground">
                            Lade in Schritt 3 eine CSV mit Kopfzeile hoch, z. B. email, vorname,
                            nachname und firma.
                          </div>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Der gewählte Wert wird an der Cursorposition eingefügt und beim Versand pro
                    Empfänger aus der CSV ersetzt.
                  </p>
                  <Textarea
                    ref={htmlTextareaRef}
                    value={html}
                    onChange={(e) => setHtml(e.target.value)}
                    className="font-mono text-xs h-80"
                  />
                </div>
                <div
                  className={
                    isPreviewFullscreen
                      ? "fixed inset-y-0 left-60 right-0 z-50 flex flex-col gap-3 bg-background p-5"
                      : "space-y-1"
                  }
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className={isPreviewFullscreen ? "text-base" : "text-xs"}>
                        E-Mail-Vorschau
                      </Label>
                      {isPreviewFullscreen && previewRecipient && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Personalisiert für {previewRecipient.email}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="inline-flex rounded-md border border-border bg-muted/30 p-0.5 text-xs">
                        <button
                          type="button"
                          onClick={() => setPreviewDevice("desktop")}
                          className={`px-2 py-0.5 rounded-sm inline-flex items-center gap-1 ${
                            previewDevice === "desktop"
                              ? "bg-background shadow-sm"
                              : "text-muted-foreground"
                          }`}
                        >
                          <Monitor className="size-3" /> Desktop
                        </button>
                        <button
                          type="button"
                          onClick={() => setPreviewDevice("mobile")}
                          className={`px-2 py-0.5 rounded-sm inline-flex items-center gap-1 ${
                            previewDevice === "mobile"
                              ? "bg-background shadow-sm"
                              : "text-muted-foreground"
                          }`}
                        >
                          <Smartphone className="size-3" /> Mobile
                        </button>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size={isPreviewFullscreen ? "default" : "sm"}
                        onClick={() => setIsPreviewFullscreen((current) => !current)}
                        title={
                          isPreviewFullscreen
                            ? "Erweiterte Vorschau schließen"
                            : "Vorschau vergrößern"
                        }
                      >
                        {isPreviewFullscreen ? (
                          <Minimize className="size-4" />
                        ) : (
                          <Maximize className="size-3.5" />
                        )}
                        <span>{isPreviewFullscreen ? "Schließen" : "Vollbild"}</span>
                      </Button>
                    </div>
                  </div>
                  {!isPreviewFullscreen && (
                    <div className="rounded-md border border-border bg-muted/20 p-2 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-[11px] text-muted-foreground">
                          CSV-Beispielzeile
                        </Label>
                        {previewRecipient && (
                          <span className="text-[10px] text-muted-foreground">
                            {filteredPreviewRecords.length < recipientCsv.records.length
                              ? `${filteredPreviewRecords.length} von `
                              : ""}
                            {recipientCsv.records.length} Zeilen
                          </span>
                        )}
                      </div>
                      {recipientCsv.records.length > 0 ? (
                        <>
                          <div className="flex items-center gap-2">
                            <Input
                              placeholder="Suche …"
                              value={previewSearch}
                              onChange={(e) => {
                                setPreviewSearch(e.target.value);
                                setPreviewRecipientIndex(0);
                              }}
                              className="h-8 text-xs flex-1"
                            />
                            <Select
                              value={previewSearchField}
                              onValueChange={(v) => {
                                setPreviewSearchField(v);
                                setPreviewRecipientIndex(0);
                              }}
                            >
                              <SelectTrigger className="h-8 text-xs w-auto min-w-[7rem] shrink-0">
                                <SelectValue placeholder="Feld" />
                              </SelectTrigger>
                              <SelectContent>
                                {searchFieldOptions.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value} className="text-xs">
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Select
                            value={String(
                              previewRecipient ? recipientCsv.records.indexOf(previewRecipient) : 0,
                            )}
                            onValueChange={(value) => {
                              const originalIndex = Number(value);
                              setPreviewRecipientIndex(originalIndex);
                            }}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="CSV-Zeile auswählen" />
                            </SelectTrigger>
                            <SelectContent>
                              {filteredPreviewRecords.map((record) => {
                                const originalIndex = recipientCsv.records.indexOf(record);
                                const displayFields = Object.entries(record.fields)
                                  .filter(([k]) => k !== "email")
                                  .map(([, v]) => v)
                                  .filter(Boolean)
                                  .join(" · ");
                                return (
                                  <SelectItem
                                    key={`${record.email}-${originalIndex}`}
                                    value={String(originalIndex)}
                                    className="text-xs"
                                  >
                                    {record.email}
                                    {displayFields ? ` — ${displayFields}` : ""}
                                  </SelectItem>
                                );
                              })}
                              {filteredPreviewRecords.length === 0 && (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                  Keine Treffer
                                </div>
                              )}
                            </SelectContent>
                          </Select>
                          {previewRecipient && (
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(previewRecipient.fields).map(([key, value]) => (
                                <span
                                  key={key}
                                  className="rounded border border-border bg-background px-1.5 py-0.5 text-[10px]"
                                >
                                  <span className="text-muted-foreground">{key}:</span>{" "}
                                  <span className="font-medium">{value || "–"}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-[11px] text-muted-foreground">
                          Lade in Schritt 3 eine CSV hoch, um eine Zeile auszuwählen und Platzhalter
                          live zu prüfen.
                        </p>
                      )}
                    </div>
                  )}
                  <div
                    className={
                      isPreviewFullscreen
                        ? "min-h-0 flex-1 overflow-auto rounded-lg border border-border bg-muted/20 p-4 shadow-sm flex justify-center"
                        : "border border-border rounded-md bg-white h-80 overflow-auto flex justify-center p-2"
                    }
                  >
                    <iframe
                      title="HTML-Vorschau"
                      srcDoc={personalizedPreviewHtml}
                      sandbox=""
                      className="bg-white border border-border/40 rounded shadow-sm"
                      style={{
                        width: previewDevice === "mobile" ? 360 : "100%",
                        maxWidth:
                          previewDevice === "mobile" ? 360 : isPreviewFullscreen ? 1100 : "100%",
                        height: "100%",
                      }}
                    />
                  </div>
                  {!isPreviewFullscreen && (
                    <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs uppercase tracking-wider text-primary inline-flex items-center gap-1.5">
                          <Mail className="size-3.5" /> Personalisierte Test-Mail
                        </Label>
                        {previewRecipient && (
                          <span className="text-[10px] text-muted-foreground truncate">
                            Daten von {previewRecipient.email}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Die Mail geht an deine Testadresse, verwendet aber alle Platzhalter des oben
                        ausgewählten Empfängers.
                      </p>
                      <Select
                        value={accountIds[0] ?? ""}
                        onValueChange={(accountId) =>
                          setAccountIds((current) => [
                            accountId,
                            ...current.filter((id) => id !== accountId),
                          ])
                        }
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue placeholder="SMTP-Konto für den Test wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {accounts.map((account: any) => (
                            <SelectItem key={account.id} value={account.id} className="text-xs">
                              {account.label} — {account.from_email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          type="email"
                          placeholder={user?.email ?? "test@example.com"}
                          value={testEmail}
                          onChange={(event) => setTestEmail(event.target.value)}
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={sendPersonalizedTest}
                          disabled={
                            sendingTest || !previewRecipient || !accountIds.length || !html.trim()
                          }
                        >
                          {sendingTest ? (
                            <Loader2 className="size-4 mr-1 animate-spin" />
                          ) : (
                            <Send className="size-4 mr-1" />
                          )}
                          Test senden
                        </Button>
                      </div>
                      {mimeValidation && (
                        <div
                          className={`rounded-md border p-2.5 text-[11px] ${
                            mimeValidation.matches
                              ? "border-primary/30 bg-primary/5"
                              : "border-destructive/30 bg-destructive/5"
                          }`}
                        >
                          <div className="flex items-center gap-1.5 font-medium">
                            {mimeValidation.matches ? (
                              <Check className="size-3.5 text-primary" />
                            ) : (
                              <AlertTriangle className="size-3.5 text-destructive" />
                            )}
                            {mimeValidation.matches
                              ? "MIME-Body stimmt exakt mit der Vorschau überein"
                              : "Abweichung zwischen MIME-Body und Vorschau erkannt"}
                          </div>
                          <p className="mt-1 text-muted-foreground">
                            Vorschau: {mimeValidation.expectedBytes} Bytes · Gesendet:{" "}
                            {mimeValidation.actualBytes} Bytes
                            {mimeValidation.firstDifference !== null
                              ? ` · Erste Abweichung an Zeichen ${mimeValidation.firstDifference + 1}`
                              : ""}
                          </p>
                          {!mimeValidation.matches && (
                            <div className="mt-2 grid gap-1 font-mono text-[10px]">
                              <p className="break-all">
                                <span className="font-sans text-muted-foreground">Vorschau: </span>
                                {mimeValidation.expectedExcerpt || "(leer)"}
                              </p>
                              <p className="break-all">
                                <span className="font-sans text-muted-foreground">MIME: </span>
                                {mimeValidation.actualExcerpt || "(leer)"}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      {!accountIds.length && (
                        <p className="text-[10px] text-muted-foreground">
                          Wähle ein SMTP-Konto, um den personalisierten Test direkt zu senden.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">6. Versand</h2>
              <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-xs space-y-1">
                <div>
                  <b>Kampagne:</b> {name}
                </div>
                <div>
                  <b>Empfänger:</b> {recipients.length}
                </div>
                <div>
                  <b>Betreffs (Rotation):</b> {subjects.length}
                </div>
                <div>
                  <b>Tracking-Pixel:</b> wird automatisch in HTML eingebaut
                </div>
                <div>
                  <b>Antworten an:</b> {replyTo.trim() || "Reply-To des jeweiligen SMTP-Kontos"}
                </div>
              </div>

              <div className="space-y-2 rounded-lg border border-primary/25 bg-primary/5 p-4">
                <Label htmlFor="wizard-reply-to">Feste Reply-To-Adresse der Kampagne</Label>
                <Input
                  id="wizard-reply-to"
                  type="email"
                  value={replyTo}
                  onChange={(event) => setReplyTo(event.target.value)}
                  placeholder="antworten@deine-domain.de"
                />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Alle Antworten landen an dieser Adresse. Sie bleibt gleich, auch wenn mehrere
                  Absender-Domains oder SMTP-Konten rotieren. Leer lassen nutzt das Reply-To des
                  jeweiligen SMTP-Kontos.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>SMTP-Konten</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="default"
                      disabled={testingAll || !accounts.length}
                      onClick={handleTestAllSmtp}
                      title="Sendet eine Testmail über jedes konfigurierte SMTP-Konto"
                    >
                      {testingAll ? (
                        <Loader2 className="size-3.5 mr-1 animate-spin" />
                      ) : (
                        <Send className="size-3.5 mr-1" />
                      )}
                      SMTP testen
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={qSaving}
                      onClick={() => handleCreatePreset("sendgrid")}
                    >
                      SendGrid anlegen
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={qSaving}
                      onClick={() => handleCreatePreset("mailersend")}
                    >
                      MailerSend anlegen
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setQuickOpen(true)}
                    >
                      SMTP anlegen
                    </Button>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Wähle ein oder mehrere Konten. Bei mehreren wird pro Mail rotiert.
                </p>
                <div className="rounded-md border border-border bg-muted/10 divide-y divide-border max-h-56 overflow-auto">
                  {accounts.map((a: any) => {
                    const checked = accountIds.includes(a.id);
                    const t = smtpTests[a.id];
                    return (
                      <label
                        key={a.id}
                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/30"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAccount(a.id)}
                        />
                        <span className="text-sm flex-1 min-w-0">
                          {a.label} <span className="text-muted-foreground">— {a.from_email}</span>
                        </span>
                        {t && (
                          <span
                            className={
                              "text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded shrink-0 " +
                              (t.status === "ok"
                                ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                                : t.status === "fail"
                                  ? "bg-red-500/15 text-red-600 dark:text-red-400"
                                  : "bg-muted text-muted-foreground")
                            }
                            title={t.msg ?? ""}
                          >
                            {t.status === "pending"
                              ? "teste…"
                              : t.status === "ok"
                                ? "OK"
                                : "Fehler"}
                          </span>
                        )}
                      </label>
                    );
                  })}
                  {!accounts.length && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      Keine SMTP-Konten vorhanden.
                    </div>
                  )}
                </div>
                {accountIds.length > 1 && (
                  <div className="space-y-1 pt-2">
                    <Label className="text-xs">Rotations-Modus</Label>
                    <Select
                      value={rotationMode}
                      onValueChange={(v) => setRotationMode(v as "round_robin" | "random")}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="round_robin">
                          Round-Robin (gleichmäßig der Reihe nach)
                        </SelectItem>
                        <SelectItem value="random">Zufällig</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">
                      {accountIds.length} Konten aktiv – ~
                      {Math.floor(recipients.length / Math.max(1, accountIds.length))} Mails pro
                      Konto bei {recipients.length} Empfängern.
                    </p>
                  </div>
                )}
                {!accounts.length && (
                  <p className="text-xs text-muted-foreground">
                    Noch kein SMTP-Konto?{" "}
                    <button
                      className="underline"
                      onClick={() => navigate({ to: "/smtp-send" })}
                      type="button"
                    >
                      Hier anlegen
                    </button>
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Pause zwischen Sends (ms)</Label>
                <Input
                  type="number"
                  min={0}
                  max={60000}
                  step={100}
                  value={throttleMs}
                  onChange={(e) => setThrottleMs(Math.max(0, Number(e.target.value) || 0))}
                />
                <p className="text-[10px] text-muted-foreground">
                  Empfohlen: 1000–3000 ms, damit SMTP-Provider nicht rate-limiten.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Tageslimit (Pacing)</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={100000}
                    step={10}
                    value={dailyLimit}
                    onChange={(e) => {
                      setDailyLimit(Math.max(1, Number(e.target.value) || 1));
                      setPacingDirty(true);
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!pacingDirty}
                    onClick={savePacing}
                  >
                    Speichern
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Kalt­akquise: 20–50 / Tag. Aufgewärmte Domain: 200–500. Über dem Limit wird der
                  Versand abgebrochen und am nächsten Tag fortgesetzt.
                </p>
              </div>

              {/* Pre-Send-Checklist */}
              <div className="rounded-md border border-border bg-muted/10 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider inline-flex items-center gap-1.5">
                    <ShieldAlert className="size-3.5" /> Pre-Send-Checkliste
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px]"
                    onClick={() => refetchReadiness()}
                  >
                    <RotateCcw className="size-3 mr-1" /> Neu prüfen
                  </Button>
                </div>
                {!readiness && <p className="text-[11px] text-muted-foreground">Wird geladen…</p>}
                {readiness && (
                  <ul className="space-y-1.5">
                    {(readiness.checks as ReadinessCheck[]).map((c) => (
                      <li
                        key={c.id}
                        className={`flex items-start gap-2 text-xs rounded px-2 py-1.5 ${
                          c.status === "ok"
                            ? "bg-emerald-500/5"
                            : c.status === "fail"
                              ? "bg-red-500/10"
                              : c.status === "warn"
                                ? "bg-amber-500/10"
                                : "bg-muted/30"
                        }`}
                      >
                        <span
                          className={`mt-0.5 inline-block size-2 rounded-full shrink-0 ${
                            c.status === "ok"
                              ? "bg-emerald-500"
                              : c.status === "fail"
                                ? "bg-red-500"
                                : c.status === "warn"
                                  ? "bg-amber-500"
                                  : "bg-muted-foreground/40"
                          }`}
                        />
                        <div className="flex-1">
                          <div className="font-medium">{c.label}</div>
                          <div className="text-muted-foreground text-[11px] leading-snug">
                            {c.detail}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[10px] text-muted-foreground pt-1">
                  Warnungen blockieren den Versand nicht — du bekommst aber vorher noch eine
                  Bestätigung.
                </p>
              </div>

              <details className="rounded-md border border-border/60 bg-muted/10 p-3">
                <summary className="text-sm font-medium cursor-pointer flex items-center gap-2">
                  <Wand2 className="size-4" /> Vorschau (mit eingebautem Pixel)
                </summary>
                <pre className="mt-3 text-[10px] font-mono whitespace-pre-wrap break-all max-h-64 overflow-auto">
                  {injectPixelLocal(html, pixelUrl)}
                </pre>
              </details>

              <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
                <Label className="text-xs uppercase tracking-wider text-primary inline-flex items-center gap-1.5">
                  <Mail className="size-3.5" /> Test-Mail an mich
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  Sende dir vor dem großen Versand eine einzelne Test-Mail – so siehst du, wie deine
                  Empfänger es bekommen.
                </p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder={user?.email ?? "test@example.com"}
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                  <Button
                    variant="secondary"
                    disabled={sendingTest || !accountIds.length || !subjects.length || !campaignId}
                    onClick={sendPersonalizedTest}
                  >
                    {sendingTest ? (
                      <Loader2 className="size-4 mr-1 animate-spin" />
                    ) : (
                      <Send className="size-4 mr-1" />
                    )}
                    Test senden
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={attemptSend}
                  disabled={sending || !accountIds.length || !recipients.length || !subjects.length}
                  className="flex-1 bg-[image:var(--gradient-primary)] hover:opacity-90"
                >
                  {sending ? (
                    <Loader2 className="size-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="size-4 mr-2" />
                  )}
                  {sending
                    ? `Sende… ${progress.done}/${recipients.length}`
                    : `Jetzt an ${recipients.length} Empfänger senden`}
                </Button>
                {sending && (
                  <Button
                    variant="destructive"
                    onClick={() => {
                      stopRequestedRef.current = true;
                    }}
                  >
                    Stop
                  </Button>
                )}
              </div>
              {(progress.done > 0 || sending) && (
                <div className="text-xs text-muted-foreground">
                  ok: {progress.ok} · fehlgeschlagen: {progress.fail} · gesamt: {progress.done}/
                  {recipients.length}
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <Button
              variant="ghost"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0 || sending}
            >
              <ArrowLeft className="size-4 mr-1" /> Zurück
            </Button>
            <div className="flex items-center gap-2">
              {campaignId && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => saveDraft.mutate()}
                    disabled={saveDraft.isPending || sending}
                    className="hidden sm:inline-flex"
                  >
                    {saveDraft.isPending ? (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 size-4" />
                    )}
                    Entwurf speichern
                  </Button>
                  <span
                    className={
                      autoSaveStatus === "error"
                        ? "text-xs text-destructive"
                        : "text-xs text-muted-foreground"
                    }
                    role="status"
                  >
                    {autoSaveStatus === "saving" && "Speichert …"}
                    {autoSaveStatus === "saved" &&
                      `Gespeichert${lastAutoSavedAt ? " · " + lastAutoSavedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : ""}`}
                    {autoSaveStatus === "error" && "Fehler"}
                  </span>
                </>
              )}
              {step < STEPS.length - 1 ? (
                <Button
                  onClick={() => void goToNextStep()}
                  disabled={!canNext}
                  className="bg-[image:var(--gradient-primary)] hover:opacity-90"
                >
                  Weiter <ArrowRight className="size-4 ml-1" />
                </Button>
              ) : (
                <Button variant="outline" onClick={() => navigate({ to: "/campaigns" })}>
                  Zu Kampagnen
                </Button>
              )}
            </div>
          </div>
        </Card>
        <WizardCoach
          step={step}
          name={name}
          desc={desc}
          recipientsCount={recipients.length}
          recipientsSample={recipients.slice(0, 5)}
          subjects={subjects}
          htmlSnippet={html}
          smtpAccountsCount={accountIds.length}
          throttleMs={throttleMs}
          onResult={setCoachResult}
          onApplyFix={applyFix}
        />
      </div>
      <Dialog open={preflightOpen} onOpenChange={setPreflightOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="size-5 text-amber-500" /> Inbox-Pre-Flight-Check
            </DialogTitle>
            <DialogDescription>
              Die KI prüft deine Kampagne vor dem Versand auf Spam-Risiken. Ziel: Inbox, nicht
              Spam-Ordner.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {preflightLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="size-4 animate-spin" /> KI analysiert Betreff, HTML, Liste &
                Versand-Setup…
              </div>
            )}
            {preflightResult && (
              <>
                <div className="flex items-end gap-3 border-b border-border/40 pb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Inbox-Wahrscheinlichkeit
                    </div>
                    <div
                      className={`text-4xl font-bold leading-none ${
                        preflightResult.score >= 80
                          ? "text-emerald-500"
                          : preflightResult.score >= 60
                            ? "text-amber-500"
                            : "text-red-500"
                      }`}
                    >
                      {preflightResult.score}
                      <span className="text-base text-muted-foreground">/100</span>
                    </div>
                  </div>
                  <p className="text-sm text-foreground/90 flex-1">{preflightResult.verdict}</p>
                </div>
                {preflightResult.issues.length > 0 && (
                  <div className="space-y-2 max-h-72 overflow-auto">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                      <AlertTriangle className="size-3.5" /> Diese Punkte solltest du vor dem
                      Versand fixen
                    </div>
                    {preflightResult.issues.map((iss, i) => (
                      <div
                        key={i}
                        className={`rounded border px-3 py-2 text-xs ${
                          iss.severity === "critical"
                            ? "bg-red-500/10 border-red-500/40"
                            : iss.severity === "high"
                              ? "bg-orange-500/10 border-orange-500/40"
                              : iss.severity === "medium"
                                ? "bg-amber-500/10 border-amber-500/40"
                                : "bg-sky-500/10 border-sky-500/40"
                        }`}
                      >
                        <div className="font-semibold uppercase tracking-wider text-[9px] opacity-70">
                          {iss.severity}
                        </div>
                        <div className="font-medium mt-0.5">{iss.title}</div>
                        <div className="text-muted-foreground mt-1 leading-snug">{iss.fix}</div>
                        {iss.action && (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="mt-2 h-7 text-xs"
                            onClick={() => {
                              applyFix(iss.action!);
                              setPreflightResult((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      issues: prev.issues.filter((_, idx) => idx !== i),
                                    }
                                  : prev,
                              );
                            }}
                          >
                            <Wand2Icon className="size-3 mr-1" />
                            {iss.action.label || "Fix anwenden"}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {preflightResult.next_action && (
                  <div className="rounded-md border border-primary/40 bg-primary/10 p-2 text-xs">
                    <span className="font-semibold text-primary uppercase tracking-wider text-[9px] mr-1">
                      Nächste Aktion:
                    </span>
                    {preflightResult.next_action}
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setPreflightOpen(false)}>
              Abbrechen & verbessern
            </Button>
            <Button
              variant="destructive"
              disabled={preflightLoading}
              onClick={() => {
                setPreflightOpen(false);
                runSend();
              }}
            >
              Trotzdem jetzt senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>SMTP-Konto anlegen</DialogTitle>
            <DialogDescription>
              Host, Port, TLS und Benutzername sind vorausgefüllt. Bitte gib die From-Adresse an.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Preset</Label>
              <Select value={qPreset} onValueChange={(v) => applyPreset(v as PresetKey)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mailersend">MailerSend</SelectItem>
                  <SelectItem value="sendgrid">SendGrid</SelectItem>
                  <SelectItem value="kanzlei">Kanzlei Rechtspartnerschaft</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Label</Label>
              <Input value={qLabel} onChange={(e) => setQLabel(e.target.value)} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Host</Label>
                <Input value={qHost} onChange={(e) => setQHost(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Port</Label>
                <Input
                  type="number"
                  value={qPort}
                  onChange={(e) => setQPort(Number(e.target.value) || 0)}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={qTls} onChange={(e) => setQTls(e.target.checked)} />
              TLS (STARTTLS / SSL)
            </label>
            <div className="space-y-1">
              <Label className="text-xs">Username</Label>
              <Input value={qUser} onChange={(e) => setQUser(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Passwort</Label>
              <Input type="password" value={qPass} onChange={(e) => setQPass(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">From-Adresse *</Label>
                <Input
                  type="email"
                  placeholder="absender@deinedomain.de"
                  value={qFromEmail}
                  onChange={(e) => setQFromEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">From-Name</Label>
                <Input
                  placeholder="Optional"
                  value={qFromName}
                  onChange={(e) => setQFromName(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setQuickOpen(false)} disabled={qSaving}>
              Abbrechen
            </Button>
            <Button onClick={handleQuickCreate} disabled={qSaving || !qFromEmail}>
              {qSaving && <Loader2 className="size-4 mr-2 animate-spin" />}
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
